import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom'
import { ErrorBoundary } from './components/ErrorBoundary'
import { I18nProvider } from './i18n/I18nProvider'
import {
  localizedRoutes,
  getLocalizedPath,
  supportedLanguages,
  type SupportedLanguage,
} from './i18n/routes'
import { AppShell } from './layout/AppShell'
import { ComponentSandbox } from './pages/ComponentSandbox'
import { ServiceRequestPage, ServicesPage } from './components/editorial/EditorialPages'
import { HomePage } from './pages/HomePage'
import { InventoryPage } from './pages/InventoryPage'
import { RoutePlaceholder } from './pages/RoutePlaceholder'
import { VehicleDetailPage } from './pages/VehicleDetailPage'
import { ContactPage } from './pages/ContactPage'
import { SellVehiclePage } from './pages/SellVehiclePage'
import { PrivacyPage } from './pages/PrivacyPage'
import { historicalRoutes } from './data/content-routes'
import { demoVehicles } from './data/vehicles'
import { AdminArea } from './admin/AdminArea'

function isSupportedLanguage(value: string | undefined): value is SupportedLanguage {
  return Boolean(value && supportedLanguages.some((language) => language === value))
}

function LegacyRedirect() {
  const { pathname } = useLocation()
  const historical = historicalRoutes.find((route) => route.legacyPath === pathname)

  if (!historical) return <Navigate to="/it" replace />

  if (historical.legacyPath.startsWith('/scheda-veicolo/')) {
    const [, , vehicleId, , legacySlug] = historical.legacyPath.split('/')
    const current = demoVehicles.find((vehicle) => vehicle.id === vehicleId)
    const slug = current?.slug ?? legacySlug
    return <Navigate to={getLocalizedPath('vehicle', 'it', { slug })} replace />
  }

  // FAQ was explicitly excluded from this build; retain the historical URL as
  // a redirect to the verified contact flow rather than publishing an empty FAQ.
  if (historical.canonicalRoute === 'faq') return <Navigate to={getLocalizedPath('contacts', 'it')} replace />
  if (historical.canonicalRoute) return <Navigate to={getLocalizedPath(historical.canonicalRoute, 'it')} replace />
  return <Navigate to={getLocalizedPath('services', 'it')} replace />
}

function LocalizedApplication() {
  const { language: languageParam } = useParams()
  const { pathname } = useLocation()
  if (!isSupportedLanguage(languageParam)) return <LegacyRedirect />

  const language = languageParam
  if (pathname === `/${language}/login` || pathname === `/${language}/admin` || pathname.startsWith(`/${language}/admin/`)) {
    return <I18nProvider language={language}><ErrorBoundary><AdminArea /></ErrorBoundary></I18nProvider>
  }
  return (
    <I18nProvider language={language}>
      <ErrorBoundary>
        <AppShell>
          <Routes>
            <Route index element={<HomePage />} />
            <Route path="sandbox" element={<ComponentSandbox />} />
            <Route path={localizedRoutes.vehicles[language]} element={<InventoryPage />} />
            <Route path={localizedRoutes.vehicle[language]} element={<VehicleDetailPage />} />
            <Route path={localizedRoutes.services[language]} element={<ServicesPage />} />
            <Route path={localizedRoutes.shortRental[language]} element={<ServiceRequestPage route="shortRental" />} />
            <Route path={localizedRoutes.booking[language]} element={<ServiceRequestPage route="booking" />} />
            <Route path={localizedRoutes.sellVehicle[language]} element={<SellVehiclePage />} />
            <Route path={localizedRoutes.contacts[language]} element={<ContactPage />} />
            <Route path={localizedRoutes.privacy[language]} element={<PrivacyPage />} />
            <Route path="chi-siamo" element={<Navigate to={getLocalizedPath('contacts', language)} replace />} />
            <Route path="about-us" element={<Navigate to={getLocalizedPath('contacts', language)} replace />} />
            <Route path="noleggio-lungo" element={<Navigate to={getLocalizedPath('services', language)} replace />} />
            <Route path="long-term-rental" element={<Navigate to={getLocalizedPath('services', language)} replace />} />
            <Route path="*" element={<RoutePlaceholder titleKey="common.notAvailable" />} />
          </Routes>
        </AppShell>
      </ErrorBoundary>
    </I18nProvider>
  )
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/it/login" replace />} />
      <Route path="/admin" element={<Navigate to="/it/admin" replace />} />
      <Route path="/" element={<Navigate to="/it" replace />} />
      <Route path="/noleggio-lungo" element={<Navigate to="/it/servizi" replace />} />
      <Route path="/:language/*" element={<LocalizedApplication />} />
      <Route path="*" element={<LegacyRedirect />} />
    </Routes>
  )
}
